import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Download, Search } from 'lucide-react';

const statusPill = (s) => {
  if (s === 'generated') return 'status-approved';
  if (s === 'cancelled') return 'status-rejected';
  return 'status-pending';
};

const money = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MyInvoices = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [params, setParams] = useSearchParams();
  const initialOpenId = params.get('open') || null;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/invoices/mine');
        setItems(data || []);
        if (initialOpenId) {
          const hit = (data || []).find(i => i.id === initialOpenId);
          if (hit) setDetail(hit);
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = q
    ? items.filter(i => (i.invoice_number || '').toLowerCase().includes(q.toLowerCase()) || (i.plan_snapshot?.name || '').toLowerCase().includes(q.toLowerCase()))
    : items;

  const download = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(inv.invoice_number || 'invoice').replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download invoice'); }
  };

  return (
    <div className='space-y-5' data-testid='my-invoices-page'>
      <div className='flex items-start justify-between flex-wrap gap-3'>
        <div>
          <h1 className='text-2xl font-semibold tracking-tight'>Invoices</h1>
          <p className='text-muted-foreground text-sm'>Download GST-compliant tax invoices and credit notes issued to your account.</p>
        </div>
        <Link to='/dashboard/billing' className='text-sm text-primary hover:underline'>Back to Billing</Link>
      </div>

      <div className='relative max-w-sm'>
        <Search className='h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2' />
        <Input placeholder='Search invoice #, plan…' className='pl-9' value={q} onChange={(e) => setQ(e.target.value)} data-testid='invoices-search' />
      </div>

      {loading ? (
        <Card className='p-10 text-center text-muted-foreground'>Loading…</Card>
      ) : filtered.length === 0 ? (
        <Card className='p-10 text-center text-muted-foreground'>No invoices yet. Invoices appear here after any successful paid purchase.</Card>
      ) : (
        <Card className='p-0 overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm' data-testid='invoices-table'>
              <thead className='text-left bg-secondary/40'>
                <tr>
                  <th className='py-2.5 px-3'>Invoice #</th>
                  <th className='py-2.5 px-3'>Date</th>
                  <th className='py-2.5 px-3'>Plan</th>
                  <th className='py-2.5 px-3 text-right'>Taxable</th>
                  <th className='py-2.5 px-3 text-right'>Tax</th>
                  <th className='py-2.5 px-3 text-right'>Total</th>
                  <th className='py-2.5 px-3'>Status</th>
                  <th className='py-2.5 px-3'></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const t = inv.tax_snapshot || {};
                  return (
                    <tr key={inv.id} className='border-t hover:bg-secondary/20' data-testid={`invoice-row-${inv.id}`}>
                      <td className='py-2.5 px-3 font-mono text-xs'>
                        <button
                          type='button'
                          className='text-primary hover:underline'
                          onClick={() => { setDetail(inv); const p = new URLSearchParams(params); p.set('open', inv.id); setParams(p, { replace: true }); }}
                          data-testid={`invoice-open-${inv.id}`}
                        >
                          {inv.invoice_number}
                        </button>
                        {inv.doc_type === 'credit_note' && <span className='ml-1 text-[10px] text-red-600 uppercase'>Credit Note</span>}
                      </td>
                      <td className='py-2.5 px-3 text-muted-foreground'>{(inv.invoice_date || '').slice(0, 10)}</td>
                      <td className='py-2.5 px-3'>{inv.plan_snapshot?.name || '—'}</td>
                      <td className='py-2.5 px-3 text-right tabular-nums'>{money(t.taxable_amount)}</td>
                      <td className='py-2.5 px-3 text-right tabular-nums'>{money(t.total_tax)}</td>
                      <td className='py-2.5 px-3 text-right tabular-nums font-semibold'>{money(t.grand_total)}</td>
                      <td className='py-2.5 px-3'>
                        <span className={`inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium ${statusPill(inv.status)}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className='py-2.5 px-3'>
                        <Button size='sm' variant='outline' onClick={() => download(inv)} data-testid={`invoice-download-${inv.id}`}>
                          <Download className='h-3.5 w-3.5 mr-1' /> PDF
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); const p = new URLSearchParams(params); p.delete('open'); setParams(p, { replace: true }); } }}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <FileText className='h-5 w-5' /> {detail?.invoice_number}
            </DialogTitle>
            <DialogDescription>Tax invoice details — download the PDF using the button below.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className='space-y-4 text-sm' data-testid='invoice-detail-modal'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <div className='text-xs uppercase tracking-wider text-muted-foreground'>Bill from</div>
                  <div className='font-semibold'>{detail.biz_snapshot?.legal_name}</div>
                  <div className='text-xs text-muted-foreground'>GSTIN: {detail.biz_snapshot?.gstin || '—'}</div>
                  <div className='text-xs text-muted-foreground'>{detail.biz_snapshot?.city}, {detail.biz_snapshot?.state}</div>
                </div>
                <div>
                  <div className='text-xs uppercase tracking-wider text-muted-foreground'>Bill to</div>
                  <div className='font-semibold'>{detail.customer_snapshot?.name}</div>
                  {detail.customer_snapshot?.company_name && <div className='text-xs'>{detail.customer_snapshot.company_name}</div>}
                  <div className='text-xs text-muted-foreground'>GSTIN: {detail.customer_snapshot?.gstin || '—'}</div>
                  <div className='text-xs text-muted-foreground'>{detail.customer_snapshot?.state || detail.place_of_supply_state}</div>
                </div>
              </div>

              <div className='rounded-lg border overflow-hidden'>
                <table className='w-full text-sm'>
                  <thead className='bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground'>
                    <tr>
                      <th className='py-2 px-3'>Plan</th>
                      <th className='py-2 px-3 text-right'>Taxable</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className='border-t'>
                      <td className='py-2 px-3'>
                        <div className='font-medium'>{detail.plan_snapshot?.name}</div>
                        <div className='text-xs text-muted-foreground'>{detail.plan_snapshot?.duration_days ? `${detail.plan_snapshot.duration_days} days` : ''}</div>
                      </td>
                      <td className='py-2 px-3 text-right tabular-nums'>{money(detail.tax_snapshot?.taxable_amount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className='grid grid-cols-2 gap-2 text-sm'>
                <div className='text-muted-foreground'>Place of Supply</div>
                <div className='text-right'>{detail.place_of_supply_state} ({detail.place_of_supply_state_code})</div>
                <div className='text-muted-foreground'>Taxable amount</div>
                <div className='text-right tabular-nums'>{money(detail.tax_snapshot?.taxable_amount)}</div>
                {detail.tax_snapshot?.is_intra_state ? (
                  <>
                    <div className='text-muted-foreground'>CGST @ {detail.tax_snapshot?.cgst_rate_pct}%</div>
                    <div className='text-right tabular-nums'>{money(detail.tax_snapshot?.cgst_amount)}</div>
                    <div className='text-muted-foreground'>SGST @ {detail.tax_snapshot?.sgst_rate_pct}%</div>
                    <div className='text-right tabular-nums'>{money(detail.tax_snapshot?.sgst_amount)}</div>
                  </>
                ) : (
                  <>
                    <div className='text-muted-foreground'>IGST @ {detail.tax_snapshot?.igst_rate_pct}%</div>
                    <div className='text-right tabular-nums'>{money(detail.tax_snapshot?.igst_amount)}</div>
                  </>
                )}
                <div className='font-semibold border-t pt-2 mt-1'>Grand total</div>
                <div className='font-semibold border-t pt-2 mt-1 text-right tabular-nums'>{money(detail.tax_snapshot?.grand_total)}</div>
              </div>

              <div className='grid grid-cols-2 gap-2 text-xs text-muted-foreground border-t pt-3'>
                <div>Payment method: <span className='text-foreground'>{detail.payment_method}</span></div>
                <div>Payment status: <span className='text-foreground'>{detail.payment_status}</span></div>
                <div className='col-span-2'>Reference: <span className='font-mono text-foreground'>{detail.payment_reference}</span></div>
                {detail.original_invoice_number && <div className='col-span-2'>Against invoice: <span className='font-mono text-foreground'>{detail.original_invoice_number}</span></div>}
              </div>

              <div className='flex justify-end'>
                <Button onClick={() => download(detail)} data-testid='invoice-detail-download'>
                  <Download className='h-4 w-4 mr-1.5' /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyInvoices;
